import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";

export default function ReviewForm({ bookingRef, email, defaultName, existingReview, onSubmitted }) {
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(existingReview?.comment || "");
  const [displayName, setDisplayName] = useState(existingReview?.display_name || defaultName || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(Boolean(existingReview));

  async function submit(e) {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Pick a rating.");
      return;
    }
    if (comment.trim().length < 4) {
      toast.error("Please write a short comment.");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post("/reviews", {
        booking_ref: bookingRef,
        email,
        rating,
        comment: comment.trim(),
        display_name: displayName.trim(),
      });
      setDone(true);
      onSubmitted?.();
      toast.success("Thank you — your review has been submitted.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="track-review-form"
      className="border border-white/10 bg-[#0A0A0A] p-6 md:p-8"
    >
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
        Leave a review
      </div>
      <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white">
        {done ? "Thank you." : "How did we do?"}
      </h3>
      <p className="text-zinc-400 text-sm mt-2">
        {done
          ? "Your review has been sent to our team. If featured, it'll appear on the homepage."
          : "Your feedback helps other customers find us. We only publish reviews with your booking reference."}
      </p>

      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="done"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 flex items-center gap-3 text-[#E60000]"
          >
            <CheckCircle2 className="w-5 h-5" />
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Review received</div>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 space-y-5"
          >
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
                Rating
              </label>
              <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = (hover || rating) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      data-testid={`track-review-star-${n}`}
                      onMouseEnter={() => setHover(n)}
                      onClick={() => setRating(n)}
                      className="p-1 focus:outline-none"
                      aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    >
                      <Star
                        className={`w-9 h-9 transition-colors ${
                          active ? "text-[#E60000] fill-[#E60000]" : "text-zinc-700 hover:text-zinc-500"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
                Display name (shown publicly)
              </label>
              <input
                data-testid="track-review-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                maxLength={120}
                className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors"
              />
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
                Your review
              </label>
              <textarea
                data-testid="track-review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell others how the move went…"
                rows={4}
                maxLength={1000}
                className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors resize-none"
              />
              <div className="text-[10px] text-zinc-600 mt-1 text-right">{comment.length}/1000</div>
            </div>

            <button
              type="submit"
              data-testid="track-review-submit"
              disabled={submitting || rating < 1 || comment.trim().length < 4}
              className="btn-primary disabled:opacity-40"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Star className="w-5 h-5" />}
              Submit review
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
